import React, { useState, useEffect } from 'react';
import { Calculator, ThermometerSun, Thermometer, Waves, Hand, Cog, Droplets } from 'lucide-react';

type MixingMethod = 'hand' | 'stand' | 'pro';

interface TemperatureInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  icon: React.ElementType;
  readOnly?: boolean;
  unit: '°C' | '°F';
}

function TemperatureInput({ label, value, onChange, icon: Icon, readOnly, unit }: TemperatureInputProps) {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-white/90">
        {label}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-5 w-5 text-white/50" />
        </div>
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          className={`w-full pl-10 pr-12 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20 ${
            readOnly ? 'cursor-not-allowed' : ''
          }`}
          placeholder="Enter temperature"
        />
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <span className="text-white/50">{unit}</span>
        </div>
      </div>
    </div>
  );
}

export function WaterTemperatureCalculator() {
  const [unit, setUnit] = useState<'°C' | '°F'>('°C');
  const [mixingMethod, setMixingMethod] = useState<MixingMethod>('hand');
  const [roomTemp, setRoomTemp] = useState('');
  const [flourTemp, setFlourTemp] = useState('');
  const [waterTemp, setWaterTemp] = useState('');

  // Convert Fahrenheit to Celsius
  const toCelsius = (fahrenheit: number) => (fahrenheit - 32) * 5/9;

  // Convert Celsius to Fahrenheit
  const toFahrenheit = (celsius: number) => (celsius * 9/5) + 32;

  // Get base temperature based on mixing method
  const getBaseTemp = () => {
    switch (mixingMethod) {
      case 'hand': return 72;
      case 'stand': return 70;
      case 'pro': return 65;
      default: return 72;
    }
  };

  // Calculate water temperature
  useEffect(() => {
    if (roomTemp && flourTemp) {
      const baseTemp = getBaseTemp();
      let roomTempC = unit === '°F' ? toCelsius(parseFloat(roomTemp)) : parseFloat(roomTemp);
      let flourTempC = unit === '°F' ? toCelsius(parseFloat(flourTemp)) : parseFloat(flourTemp);
      
      // Calculate water temperature in Celsius
      let waterTempC = baseTemp - roomTempC - flourTempC;
      
      // Convert to Fahrenheit if necessary
      let displayTemp = unit === '°F' ? toFahrenheit(waterTempC) : waterTempC;
      
      // Round to 1 decimal place
      setWaterTemp(displayTemp.toFixed(1));
    } else {
      setWaterTemp('');
    }
  }, [roomTemp, flourTemp, mixingMethod, unit]);

  return (
    <div className="space-y-6 text-white/90">
      <div className="bg-gray-600/60 p-6">

        <div className="space-y-6">
          <div className="flex items-start gap-4 p-4 bg-amber-500/10 backdrop-blur-sm rounded-xl border border-amber-500/20">
            <div className="flex-shrink-0 mt-1">
              <Droplets className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-amber-400 mb-2">About this Calculator</h3>
              <p className="text-sm text-white/90">
                The water temperature calculator assists you in achieving a Desired Dough Temperature (DDT) of 26°C/79°F by factoring in the room and flour temperatures along with the mixing method. Whether you mix by hand, use a stand mixer, or a professional mixer, this tool ensures your dough reaches the optimal temperature for fermentation.
              </p>
            </div>
          </div>
          
          {/* Temperature Unit Selection */}
          <div className="flex">
            <div className="inline-flex rounded-xl overflow-hidden border border-white/20">
              <button
                onClick={() => setUnit('°C')}
                className={`px-4 py-2 text-sm ${
                  unit === '°C' 
                    ? 'bg-amber-500 text-white' 
                    : 'text-white/70 hover:bg-white/10'
                }`}
              >
                Celsius
              </button>
              <button
                onClick={() => setUnit('°F')}
                className={`px-4 py-2 text-sm ${
                  unit === '°F' 
                    ? 'bg-amber-500 text-white' 
                    : 'text-white/70 hover:bg-white/10'
                }`}
              >
                Fahrenheit
              </button>
            </div>
          </div>

          {/* Mixing Method Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-white/90">
              Mixing Method
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button
                onClick={() => setMixingMethod('hand')}
                className={`p-4 border rounded-xl flex items-center gap-3 transition-colors ${
                  mixingMethod === 'hand'
                    ? 'border-amber-400/50 bg-gradient-to-b from-amber-400/20 to-amber-500/10'
                    : 'border-white/20 bg-white/5 hover:bg-white/10'
                }`}
              >
                <Hand className="w-5 h-5 text-amber-400" />
                <span className="text-white">By Hand</span>
              </button>
              <button
                onClick={() => setMixingMethod('stand')}
                className={`p-4 border rounded-xl flex items-center gap-3 transition-colors ${
                  mixingMethod === 'stand'
                    ? 'border-amber-400/50 bg-gradient-to-b from-amber-400/20 to-amber-500/10'
                    : 'border-white/20 bg-white/5 hover:bg-white/10'
                }`}
              >
                <Cog className="w-5 h-5 text-amber-400" />
                <span className="text-white">Stand Mixer</span>
              </button>
              <button
                onClick={() => setMixingMethod('pro')}
                className={`p-4 border rounded-xl flex items-center gap-3 transition-colors ${
                  mixingMethod === 'pro'
                    ? 'border-amber-400/50 bg-gradient-to-b from-amber-400/20 to-amber-500/10'
                    : 'border-white/20 bg-white/5 hover:bg-white/10'
                }`}
              >
                <Cog className="w-5 h-5 text-amber-400" />
                <span className="text-white">PRO Mixer</span>
              </button>
            </div>
          </div>

          {/* Temperature Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <TemperatureInput
              label="Room Temperature"
              value={roomTemp}
              onChange={setRoomTemp}
              icon={ThermometerSun}
              unit={unit}
            />
            <TemperatureInput
              label="Flour Temperature"
              value={flourTemp}
              onChange={setFlourTemp}
              icon={Thermometer}
              unit={unit}
            />
          </div>

          {/* Result */}
          <div className="mt-8">
            <TemperatureInput
              label="Required Water Temperature"
              value={waterTemp}
              onChange={() => {}}
              icon={Waves}
              readOnly
              unit={unit}
            />
          </div>

          {/* Explanation */}
          <div className="p-4 bg-amber-500/10 backdrop-blur-sm rounded-xl mt-6">
            <h4 className="text-amber-400 font-medium mb-2">How it works</h4>
            <p className="text-sm text-white/80">
              This calculator uses the Desired Dough Temperature (DDT) formula to determine the correct water temperature.
              The base temperature varies depending on your mixing method:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-white/80">
              <li>• Hand mixing: {unit === '°C' ? '72°C' : '161.6°F'}</li>
              <li>• Stand mixer: {unit === '°C' ? '70°C' : '158°F'}</li>
              <li>• Professional mixer: {unit === '°C' ? '65°C' : '149°F'}</li>
            </ul>
            <p className="mt-2 text-sm text-white/80">
              The formula accounts for the friction factor of each mixing method to achieve the optimal dough temperature
              for fermentation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}